/* eslint-disable max-lines */
import { Button, Textarea, Toggle } from '@hakimo-ui/shared/ui-base';
import {
  ClockIcon,
  TrashIcon,
  VideoCameraIcon,
} from '@heroicons/react/24/outline';
import { SOPException, TimeRange } from '@hakimo-ui/hakimo/types';
import { useState } from 'react';
import CameraSelectionModal from './camera-selection-modal/CameraSelectionModal';
import CameraLabels from './camera-labels/CameraLabels';
import TimeRangeSelectionModal from './time-range-selection-modal/TimeRangeSelectionModal';
import TimeRangeLabels from './time-range-labels/TimeRangeLabels';

interface Props {
  exceptions: SOPException[];
  onChange: (val: SOPException[]) => void;
  locationId?: string;
}

export function WorkflowFormExceptions(props: Props) {
  const { exceptions, onChange, locationId } = props;
  const [cameraModalOpen, setCameraModalOpen] = useState(false);
  const [selectedExceptionIndex, setSelectedExceptionIndex] = useState<
    number | null
  >(null);
  const [timeRangeModalOpen, setTimeRangeModalOpen] = useState(false);
  const [
    selectedExceptionIndexForTimeRange,
    setSelectedExceptionIndexForTimeRange,
  ] = useState<number | null>(null);

  const onInputChange = (index: number) => (val: string) => {
    const updatedExceptions = [...exceptions];
    updatedExceptions[index] = { ...updatedExceptions[index], text: val };
    onChange(updatedExceptions);
  };

  const onToggleChange = (index: number) => (enabled: boolean) => {
    const updatedExceptions = [...exceptions];
    updatedExceptions[index] = {
      ...updatedExceptions[index],
      isRelevantForAiOperator: enabled,
    };
    onChange(updatedExceptions);
  };

  const onDeleteException = (index: number) => () => {
    const updatedExceptions = [...exceptions];
    updatedExceptions.splice(index, 1);
    onChange(updatedExceptions);
  };

  const onCameraIconClick = (index: number) => () => {
    if (!locationId) return;
    setSelectedExceptionIndex(index);
    setCameraModalOpen(true);
  };

  const onCameraSelectionSubmit = (selectedCameraNames: string[]) => {
    if (selectedExceptionIndex !== null) {
      const updatedExceptions = [...exceptions];
      updatedExceptions[selectedExceptionIndex] = {
        ...updatedExceptions[selectedExceptionIndex],
        cameras: selectedCameraNames,
      };
      onChange(updatedExceptions);
    }
  };

  const onCameraModalClose = () => {
    setCameraModalOpen(false);
    setSelectedExceptionIndex(null);
  };

  const onTimeRangeIconClick = (index: number) => () => {
    setSelectedExceptionIndexForTimeRange(index);
    setTimeRangeModalOpen(true);
  };

  const onTimeRangeSelectionSubmit = (timeRange: TimeRange | null) => {
    if (selectedExceptionIndexForTimeRange !== null) {
      const updatedExceptions = [...exceptions];
      updatedExceptions[selectedExceptionIndexForTimeRange] = {
        ...updatedExceptions[selectedExceptionIndexForTimeRange],
        timeRange: timeRange || undefined,
      };
      onChange(updatedExceptions);
    }
  };

  const onTimeRangeModalClose = () => {
    setTimeRangeModalOpen(false);
    setSelectedExceptionIndexForTimeRange(null);
  };

  return (
    <div className="space-y-4">
      {exceptions.map((exception, i) => (
        <div key={i} className="space-y-2">
          <div className="flex items-center gap-2">
            <div className="flex-1">
              <Textarea
                value={exception.text}
                onChange={onInputChange(i)}
                placeholder="Enter exception text..."
                rows={3}
              />
            </div>
            <div className="mt-2 flex flex-col items-center justify-center gap-1">
              <div className="flex-shrink-0">
                <Toggle
                  enabled={exception.isRelevantForAiOperator}
                  onChange={onToggleChange(i)}
                  label="AI Operator"
                  type="positive"
                />
              </div>
              <div className="flex items-center justify-center">
                {locationId && (
                  <div>
                    <Button
                      variant="icon"
                      onClick={onCameraIconClick(i)}
                      title="Associate Camera with this exception"
                    >
                      <VideoCameraIcon className="h-5 w-5" />
                    </Button>
                  </div>
                )}
                <div>
                  <Button
                    variant="icon"
                    onClick={onTimeRangeIconClick(i)}
                    title="Add Time Range"
                  >
                    <ClockIcon className="h-5 w-5" />
                  </Button>
                </div>
                <div>
                  <Button variant="icon" onClick={onDeleteException(i)}>
                    <TrashIcon className="h-5 w-5" />
                  </Button>
                </div>
              </div>
            </div>
          </div>
          <TimeRangeLabels timeRange={exception.timeRange} />
          <CameraLabels cameras={exception.cameras} />
        </div>
      ))}
      {cameraModalOpen && locationId && selectedExceptionIndex !== null && (
        <CameraSelectionModal
          open={cameraModalOpen}
          onClose={onCameraModalClose}
          onSubmit={onCameraSelectionSubmit}
          locationId={locationId}
          initialSelectedCameras={
            exceptions[selectedExceptionIndex]?.cameras || []
          }
        />
      )}
      {timeRangeModalOpen && selectedExceptionIndexForTimeRange !== null && (
        <TimeRangeSelectionModal
          open={timeRangeModalOpen}
          onClose={onTimeRangeModalClose}
          onSubmit={onTimeRangeSelectionSubmit}
          initialTimeRange={
            exceptions[selectedExceptionIndexForTimeRange]?.timeRange
          }
        />
      )}
    </div>
  );
}

export default WorkflowFormExceptions;
